import React, { useEffect, useRef as useReactRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getBestImageUrl } from '@/lib/utils';
import LikeButton from '@/components/LikeButton';
import type { Business, Event, Artist } from '@/features/clubs/types/types';

interface FavouritesSectionProps {
  likedClubs: Business[];
  likedEvents: Event[];
  likedArtists: Artist[];
  loading: boolean;
  error: string | null;
  unlikedClubIds: Set<string>;
  unlikedEventIds: Set<string>;
  unlikedArtistIds: Set<string>;
  onClubLikeToggle: (clubId: string, liked: boolean) => void;
  onEventLikeToggle: (eventId: string, liked: boolean) => void;
  onArtistLikeToggle: (artistId: string, liked: boolean) => void;
}

/**
 * FavouritesSection - Displays liked entities in the profile page.
 * Shows 3 horizontal scrollable sections: Clubs, Events, Artists.
 */
const FavouritesSection: React.FC<FavouritesSectionProps> = ({
  likedClubs,
  likedEvents,
  likedArtists,
  loading,
  error,
  unlikedClubIds,
  unlikedEventIds,
  unlikedArtistIds,
  onClubLikeToggle,
  onEventLikeToggle,
  onArtistLikeToggle,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, isGuest } = useAuth();
  
  const isRealUser = isAuthenticated && !isGuest;

  // All items stay visible
  const visibleClubs = likedClubs;
  const visibleEvents = likedEvents;
  const visibleArtists = likedArtists;

  if (!isRealUser) {
    return (
      <motion.div 
        className="lg:col-span-2"
        initial={{ opacity: 0, x: 50, scale: 0.95 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        exit={{ opacity: 0, x: -50, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30, mass: 0.8 }}
      >
        <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 text-center">
          <p className="text-gray-400">Please log in to view your favourites.</p>
        </div>
      </motion.div>
    );
  }

  // Content skeleton for loading state
  const LoadingSkeleton = () => (
    <div className="flex-1 space-y-6 overflow-hidden">
      {['Clubs', 'Events', 'Artists'].map((section, sectionIndex) => (
        <motion.div 
          key={section}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + sectionIndex * 0.15, duration: 0.4 }}
        >
          {/* Section title skeleton */}
          <motion.div 
            className="h-5 w-16 bg-gray-700/60 rounded mb-3"
            animate={{ opacity: [0.5, 0.8, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: sectionIndex * 0.2 }}
          />
          
          {/* Cards skeleton row */}
          <div className="flex gap-4 overflow-hidden">
            {[1, 2, 3, 4].map((card, cardIndex) => (
              <motion.div 
                key={card} 
                className="flex-shrink-0 w-[200px] sm:w-[220px]"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ 
                  delay: 0.2 + sectionIndex * 0.15 + cardIndex * 0.05, 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30 
                }}
              >
                <motion.div 
                  className="aspect-[3/4] rounded-2xl bg-gradient-to-b from-gray-800/80 to-gray-800/40"
                  animate={{ opacity: [0.6, 0.9, 0.6] }}
                  transition={{ 
                    duration: 1.8, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: cardIndex * 0.1
                  }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );

  const hasNoFavourites = visibleClubs.length === 0 && visibleEvents.length === 0 && visibleArtists.length === 0;

  return (
    <motion.div 
      key="favourites"
      className="lg:col-span-2"
      initial={{ opacity: 0, x: 50, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: -50, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 300, damping: 30, mass: 0.8 }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-3xl p-4 sm:p-6 lg:p-8 h-auto sm:h-[600px] lg:h-[700px] flex flex-col overflow-hidden">
        {/* Header - Always visible */}
        <div className="mb-6 flex-shrink-0">
          <h3 className="text-2xl font-serif font-medium text-white">Favourites</h3>
          <div className="w-16 h-0.5 bg-violet-500 mt-2" />
        </div>

        {/* Content area - Shows loading/error/empty/data states */}
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="flex flex-col items-center justify-center flex-1 text-center py-10">
            <p className="text-red-400">{error}</p>
          </div>
        ) : hasNoFavourites ? (
          <div className="text-center py-10">
            <p className="text-gray-400">Your favourites will appear here.</p>
            <p className="text-gray-500 text-sm mt-2">Like venues, events, or artists to add them to your favourites.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-6 pr-1" style={{ scrollbarWidth: 'thin' }}>
            {/* Clubs Section */}
            {visibleClubs.length > 0 && (
              <FavouriteSection
                title="Venues"
                items={visibleClubs}
                type="businesses"
                onItemClick={(club) => navigate(`/venues/${club.handle}`)}
                onLikeToggle={onClubLikeToggle}
                isLiked={(id) => !unlikedClubIds.has(id)}
                getImageId={(club) => club.card_image_id}
                getMedia={(club) => club.media}
                getName={(club) => club.name}
                getSubtitle={(club) => `${club.location.city}, ${club.location.country}`}
              />
            )}

            {/* Events Section */}
            {visibleEvents.length > 0 && (
              <FavouriteSection
                title="Events"
                items={visibleEvents}
                type="events"
                onItemClick={(event) => navigate(`/venues/${event.business?.handle}/events/${event.id}`)}
                onLikeToggle={onEventLikeToggle}
                isLiked={(id) => !unlikedEventIds.has(id)}
                getImageId={(event) => event.card_image_id}
                getMedia={(event) => event.media}
                getName={(event) => event.name}
                getSubtitle={(event) => event.business?.name || 'Unknown Venue'}
              />
            )}

            {/* Artists Section */}
            {visibleArtists.length > 0 && (
              <FavouriteSection
                title="Artists"
                items={visibleArtists}
                type="artists"
                onItemClick={(artist) => navigate(`/venues/${artist.business?.handle}/artist/${artist.id}`)}
                onLikeToggle={onArtistLikeToggle}
                isLiked={(id) => !unlikedArtistIds.has(id)}
                getImageId={(artist) => artist.card_image_id}
                getMedia={(artist) => artist.media}
                getName={(artist) => artist.name}
                getSubtitle={(artist) => artist.music_genres?.join(', ') || ''}
              />
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Generic Favourite Section Component
interface FavouriteSectionProps<T extends { id: string }> {
  title: string;
  items: T[];
  type: 'businesses' | 'events' | 'artists';
  onItemClick: (item: T) => void;
  onLikeToggle: (id: string, liked: boolean) => void;
  isLiked: (id: string) => boolean;
  getImageId: (item: T) => string | null;
  getMedia: (item: T) => { media_id: string }[] | undefined | null;
  getName: (item: T) => string;
  getSubtitle: (item: T) => string;
}

function FavouriteSection<T extends { id: string }>({
  title,
  items,
  type,
  onItemClick,
  onLikeToggle,
  isLiked,
  getImageId,
  getMedia,
  getName,
  getSubtitle,
}: FavouriteSectionProps<T>) {
  const scrollContainerRef = useReactRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Check scroll state
  const checkScrollState = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollState();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollState);
      window.addEventListener('resize', checkScrollState);
      return () => {
        container.removeEventListener('scroll', checkScrollState);
        window.removeEventListener('resize', checkScrollState);
      };
    }
  }, [items]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-lg font-medium text-white">{title}</h4>
        <div className="flex gap-2">
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide pt-3 pb-2 -mt-3"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <AnimatePresence mode="popLayout">
          {items.map((item) => (
            <motion.div
              key={item.id}
              layout
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -20 }}
              whileHover={{ 
                y: -8, 
                scale: 1.02,
                transition: { type: "spring", stiffness: 400, damping: 25 }
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="flex-shrink-0 w-[200px] sm:w-[220px] cursor-pointer"
              onClick={() => onItemClick(item)}
            >
              <motion.div 
                className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-gray-800"
                whileHover={{ 
                  boxShadow: "0 20px 40px rgba(139, 92, 246, 0.15)",
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                {getBestImageUrl(getImageId(item), getMedia(item), '') !== '' ? (
                  <motion.img
                    src={getBestImageUrl(getImageId(item), getMedia(item), '/placeholder.jpg')}
                    alt={getName(item)}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.08 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <span className="text-xs text-gray-500 uppercase tracking-widest">{getName(item)}</span>
                  </div>
                )}
                
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                
                {/* Like button */}
                <LikeButton
                  entityId={item.id}
                  entityType={type}
                  isLiked={isLiked(item.id)}
                  onToggle={(liked) => onLikeToggle(item.id, liked)}
                  className="absolute top-2 right-2 z-10"
                  size="sm"
                />
                
                {/* Text overlay */}
                <motion.div 
                  className="absolute bottom-0 left-0 right-0 p-3"
                  initial={{ opacity: 0.9 }}
                  whileHover={{ opacity: 1 }}
                >
                  <h5 className="font-medium text-sm text-white truncate">{getName(item)}</h5>
                  <p className="text-xs text-gray-300 truncate">{getSubtitle(item)}</p>
                </motion.div>
              </motion.div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default FavouritesSection;
